interface DayInsertBetweenParams {
  position: 'before' | 'after';
  dayIndex: number;
  experience: any;
  previousExperience: any;
  days: any[];
  tripDuration: number;
  isBookedTrip: boolean;
  currentDayEditable: boolean;
  previousDayEditable: boolean;
  hasPartnerDayInRange: (startIndex: number) => boolean;
}

interface DayInsertBetweenState {
  targetIndex: number;
  shouldRemove: boolean;
  shouldRender: boolean;
}

// If button is before the day, it should render if the previous experience is
// different from the current experience, if they share the same
// in-experience day index (the same experience placed twice, each restarting
// its own numbering) or if there is no previous experience.
function shouldRenderBefore(
  experience: any,
  previousExperience: any,
  days: any[],
  shouldRemove: boolean,
): boolean {
  return (
    (previousExperience?.id !== experience?.id ||
      previousExperience?.dayIndex === experience?.dayIndex ||
      !previousExperience?.id) &&
    ((days?.length ?? 1) > 1 || !shouldRemove)
  );
}

// Inserting/removing a day shifts the dates of every day that follows the
// edit point without touching those days themselves. On a booked trip that
// would silently move a partner's already-confirmed booking to a different
// date, so the edit is blocked outright rather than routed through the
// draft-trip price-shift confirmation (confirmPartnerPriceShifts skips
// detection entirely once the trip is booked).
export function getDayInsertBetweenState({
  position,
  dayIndex,
  experience,
  previousExperience,
  days,
  tripDuration,
  isBookedTrip,
  currentDayEditable,
  previousDayEditable,
  hasPartnerDayInRange,
}: DayInsertBetweenParams): DayInsertBetweenState {
  const dayOffset = position === 'before' ? 0 : 1;
  const targetIndex = dayIndex + dayOffset;

  // If the experience is not set, the button should be a remove button
  const shouldRemove = position === 'before' && !experience?.id;

  const wouldShiftPartnerDay = (startIndex: number) =>
    isBookedTrip && hasPartnerDayInRange(startIndex);

  // A DMC may not touch another DMC's part of the itinerary: removing a day
  // requires the day to carry no partner product, and inserting is blocked
  // only strictly inside a partner block (both surrounding days locked).
  // Appending after the last day is always allowed. TO's config never
  // reports a foreign product, so this never blocks TO.
  const isActionAllowed = shouldRemove
    ? currentDayEditable && !wouldShiftPartnerDay(targetIndex + 1)
    : (position === 'after' || currentDayEditable || previousDayEditable) &&
      !wouldShiftPartnerDay(targetIndex);

  if (!isActionAllowed) {
    return { targetIndex, shouldRemove, shouldRender: false };
  }

  const shouldRender =
    position === 'before'
      ? shouldRenderBefore(experience, previousExperience, days, shouldRemove)
      : dayIndex === Math.max((days?.length ?? 1) - 1, tripDuration - 1);

  return { targetIndex, shouldRemove, shouldRender };
}
